import { useState, useMemo } from 'react';
import { useCrossFilter } from './CrossFilterContext';

/** Natural sort comparator: "MW-2" before "MW-10", case-insensitive */
function naturalCompare(a: string, b: string): number {
  const re = /(\d+)|(\D+)/g;
  const aParts = a.match(re) || [];
  const bParts = b.match(re) || [];
  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    if (i >= aParts.length) return -1;
    if (i >= bParts.length) return 1;
    const aP = aParts[i], bP = bParts[i];
    const aNum = /^\d+$/.test(aP), bNum = /^\d+$/.test(bP);
    if (aNum && bNum) {
      const diff = parseInt(aP, 10) - parseInt(bP, 10);
      if (diff !== 0) return diff;
    } else {
      const cmp = aP.toLowerCase().localeCompare(bP.toLowerCase());
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

interface WellItem {
  well_id: string;
  well_name: string;
  color: string;
}

interface Props {
  wells: WellItem[];
  visibleWellIds: Set<string>;
  onToggle: (wellId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function WellSelectorPanel({ wells, visibleWellIds, onToggle, onSelectAll, onDeselectAll }: Props) {
  const [search, setSearch] = useState('');
  const { filter: crossFilter, isActive: crossFilterActive } = useCrossFilter();
  const showSearch = wells.length > 15;

  const filtered = useMemo(() => {
    const sorted = [...wells].sort((a, b) => naturalCompare(a.well_name, b.well_name));
    if (!search) return sorted;
    const q = search.toLowerCase();
    return sorted.filter(w => w.well_name.toLowerCase().includes(q));
  }, [wells, search]);

  const allVisible = visibleWellIds.size === wells.length;
  const noneVisible = visibleWellIds.size === 0;

  return (
    <div className="border border-mono-800 rounded p-3 bg-mono-900/30 space-y-2">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-mono-500 tracking-wider mr-1">WELLS:</span>
          <button
            onClick={onSelectAll}
            disabled={allVisible}
            className="px-2 py-0.5 text-[9px] tracking-wider text-mono-400 hover:text-mono-200 bg-mono-800 rounded disabled:opacity-30 transition-colors"
          >
            ALL
          </button>
          <button
            onClick={onDeselectAll}
            disabled={noneVisible}
            className="px-2 py-0.5 text-[9px] tracking-wider text-mono-400 hover:text-mono-200 bg-mono-800 rounded disabled:opacity-30 transition-colors"
          >
            NONE
          </button>
        </div>
        {showSearch && (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search wells..."
            className="w-full sm:w-36 sm:ml-auto px-2 py-0.5 text-[10px] bg-mono-800 border border-mono-700 rounded text-mono-200 placeholder-mono-600 outline-none focus:border-mono-500"
          />
        )}
      </div>
      <div className="flex flex-wrap gap-1 max-h-[120px] overflow-y-auto" role="listbox" aria-label="Well selector">
        {filtered.map((w) => {
          const visible = visibleWellIds.has(w.well_id);
          const isCrossFiltered = crossFilterActive && crossFilter.well === w.well_name;
          return (
            <button
              key={w.well_id}
              role="option"
              aria-selected={visible}
              onClick={() => onToggle(w.well_id)}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] tracking-wider transition-all focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:outline-none ${
                isCrossFiltered
                  ? 'bg-mono-800 text-mono-100 ring-1 ring-cyan-500/60 shadow-[0_0_6px_rgba(6,182,212,0.3)]'
                  : visible
                    ? 'bg-mono-800 text-mono-200 hover:bg-mono-700'
                    : 'bg-mono-900 text-mono-600 hover:text-mono-400'
              }`}
              style={crossFilterActive && !isCrossFiltered ? { opacity: 0.4 } : undefined}
            >
              <span
                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: visible ? w.color : '#444',
                }}
              />
              {w.well_name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
